import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import FeedbackError from "./feedback/FeedbackError";
import FeedbackFields from "./feedback/FeedbackFields";
import FeedbackPanelSwap from "./feedback/FeedbackPanelSwap";
import FeedbackThankYou from "./feedback/FeedbackThankYou";
import { ONBOARDING_FEEDBACK_FORM } from "./feedback/forms";
import { submitFeedback, FeedbackData } from "../../services/feedback";
import { FeedbackValues } from "../../services/feedbackSubmission";

/** What the last submit attempt produced. */
type SubmitResult =
  | { status: "success" }
  | { status: "error"; message: string };

interface Props {
  isOpen: boolean;
  onSuccess: () => void;
}

/**
 * FeedbackModal - A non-dismissible modal dialog for collecting user feedback
 *
 * This modal:
 * - Cannot be closed by clicking outside (static backdrop)
 * - Cannot be closed by pressing ESC key
 * - Has no close button
 * - Must be submitted to proceed
 *
 * The body is the shared FeedbackFields renderer, but the write path stays the
 * legacy `submitFeedback()` upsert against `user_feedback` — this gate is on the
 * critical path for every new signup, so the migration to the shared renderer
 * changed rendering only.
 */
const FeedbackModal: React.FC<Props> = ({ isOpen, onSuccess }) => {
  // Same split as FeedbackDialog: `resultVisible` drives the swap, `result`
  // names which panel it lands on. Retry flips only the former, so the panel
  // fading out is still the error one.
  const [resultVisible, setResultVisible] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  // The gate has no Cancel, so a lost draft here means retyping three required
  // answers with nowhere to escape to.
  const [draft, setDraft] = useState<FeedbackValues | undefined>(undefined);

  // Empty handler to make modal non-dismissible
  const preventClose = () => {
    // Intentionally empty - prevents dialog from closing
  };

  const handleSubmit = async (values: FeedbackValues) => {
    // Named explicitly rather than cast: FeedbackData's three fields are the
    // contract with the legacy service, and a rename in forms.ts should fail
    // here rather than silently send undefined.
    const payload: FeedbackData = {
      who_are_you: String(values.who_are_you ?? ""),
      use_case: String(values.use_case ?? ""),
      referral_source: String(values.referral_source ?? ""),
    };

    setDraft(values);

    try {
      const response = await submitFeedback(payload);

      if (!response.success) {
        throw new Error(
          response.error || "Failed to submit feedback. Please try again.",
        );
      }
      // The gate stays up on the confirmation panel; the user lifts it with
      // Done. This replaces a toast plus a 500ms timer that raced the modal's
      // teardown.
      setResult({ status: "success" });
    } catch (error: any) {
      // Caught, not rethrown: a failed submit here used to leave a red banner
      // over a form that still looked submittable. The gate cannot be dismissed
      // and the answers are required, so the one thing the user needs is an
      // unambiguous "that did not send" and a way to send it again.
      setResult({
        status: "error",
        message:
          error?.message || "Something went wrong. Please try again shortly.",
      });
    }
    setResultVisible(true);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={preventClose}
        // @ts-ignore - static prop prevents backdrop close
        static
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/* Modal container. Pinned to the viewport with a 16px gutter; the body
            scrolls instead of the page when the form outgrows the screen. Same
            treatment as FeedbackDialog so the two forms behave identically. */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="flex max-h-full w-full max-w-3xl transform flex-col overflow-hidden rounded-2xl bg-white [.dark_&]:bg-zinc-900 text-left shadow-xl transition-all">
              {/* Same flex-shrink treatment as FeedbackDialog: the form fits
                  itself to the window, and only scrolls as a last resort. */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <FeedbackPanelSwap
                  showResult={resultVisible}
                  formPanel={
                    <FeedbackFields
                      form={ONBOARDING_FEEDBACK_FORM}
                      initialValues={draft}
                      onDraftChange={setDraft}
                      onSubmit={handleSubmit}
                    />
                  }
                  resultPanel={
                    result?.status === "error" ? (
                      <FeedbackError
                        message={result.message}
                        onRetry={() => setResultVisible(false)}
                      />
                    ) : (
                      <FeedbackThankYou
                        message="You're all set. What you shared helps us decide what to fix, improve, and build next."
                        doneLabel="Get started"
                        onDone={onSuccess}
                      />
                    )
                  }
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FeedbackModal;

/**
 * Is this modal on screen right now?
 *
 * Shared by App.tsx, which renders it, and the nudge's do-not-disturb gate,
 * which must never draw over it. A plain function rather than a hook so it
 * survives test files that mock `hooks/useAuth` wholesale.
 *
 * `feedbackSubmitted` is null while the lookup is in flight, so only an
 * explicit `false` means "not submitted".
 */
export const isLegacyFeedbackModalOpen = (auth: {
  session: unknown;
  feedbackSubmitted: boolean | null;
}): boolean => !!auth.session && auth.feedbackSubmitted === false;
